import Column from '@app/components/layout/Column'
import { Form } from '@app/components/core/form/Form'
import { TextAreaField } from '@app/components/core/form/fields/TextAreaField'
import { TextField } from '@app/components/core/form/fields/TextField'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useMemo } from 'react'
import { useClient } from '@based/sdk/react'
import { useToast } from '@app/util/hooks/useToast'
import { useEditContainerClose } from '../shared/useEditContainerClose'
import { FieldEditActions } from '../shared/FieldEditActions'
import type { UseFormReturn } from 'react-hook-form'
import { enrich } from '@i18n/localizeHelper'
import {
  createNameAndSloganSchema,
  DESCRIPTION_MIN_LENGTH,
  type NameAndSloganFormValues,
} from '@app/pages/dashboard/initiative/shared/validation/initiativeValidation'

export type NameAndSloganEditProps = {
  initiativeId: number
  title: string
  slogan: string
}

type CreationLabels = {
  nameLabel: string
  sloganLabel: string
  namePlaceholder: string
  sloganPlaceholder: string
}

const NameAndSloganFormBody = ({
  methods,
  title,
  slogan,
  creation,
  onCancel,
}: {
  methods: UseFormReturn<NameAndSloganFormValues>
  title: string
  slogan: string
  creation: CreationLabels
  onCancel: () => void
}) => {
  const { reset, formState, trigger } = methods

  useEffect(() => {
    reset({ title, description: slogan })
    void trigger()
  }, [title, slogan, reset, trigger])

  return (
    <Column gap="1rem" alignItems="stretch">
      <TextField<NameAndSloganFormValues>
        name="title"
        label={creation.nameLabel}
        required={true}
        placeholder={creation.namePlaceholder}
      />
      <TextAreaField<NameAndSloganFormValues>
        name="description"
        label={creation.sloganLabel}
        placeholder={creation.sloganPlaceholder}
      />
      <FieldEditActions
        onCancel={onCancel}
        saveDisabled={!formState.isValid || formState.isSubmitting}
      />
    </Column>
  )
}

export const NameAndSloganEdit = ({
  initiativeId,
  title,
  slogan,
}: NameAndSloganEditProps) => {
  const close = useEditContainerClose()
  if (!close) {
    throw new Error('NameAndSloganEdit requires an EditContainer ancestor')
  }
  const client = useClient()
  const { showFailToast, showSuccessToast } = useToast()
  const common = useTranslations().app.common
  const creation = useTranslations().app.dashboard.initiative.creation
  const view = useTranslations().app.dashboard.initiative.view
  const formValidation = useTranslations().app.form.validation

  const schema = useMemo(
    () =>
      createNameAndSloganSchema(
        view.initiativeNameMinLength,
        enrich(formValidation.minLength, DESCRIPTION_MIN_LENGTH),
      ),
    [view.initiativeNameMinLength, formValidation.minLength],
  )

  const onSubmit = useCallback(
    (data: NameAndSloganFormValues) => {
      void client
        .call('edit-initiative', {
          id: initiativeId,
          value: {
            title: data.title,
            description: data.description,
          },
        })
        .then((success) => {
          if (success) {
            showSuccessToast(common.toastSuccess)
            void client.call('calculate-slug-initiative', {
              id: initiativeId,
            })
            close()
          } else {
            showFailToast()
          }
        })
        .catch(() => showFailToast())
    },
    [
      client,
      initiativeId,
      close,
      showFailToast,
      showSuccessToast,
      common.toastSuccess,
    ],
  )

  return (
    <Form<NameAndSloganFormValues>
      schema={schema}
      defaultValues={{ title, description: slogan }}
      mode="onChange"
      onSubmit={onSubmit}
    >
      {(methods) => (
        <NameAndSloganFormBody
          methods={methods}
          title={title}
          slogan={slogan}
          creation={creation}
          onCancel={() => {
            methods.reset({ title, description: slogan })
            close()
          }}
        />
      )}
    </Form>
  )
}
